import { Link as RouterLink } from "react-router-dom";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import StorefrontLayout from "components/storefront/StorefrontLayout";
import useStorefrontCopy from "hooks/useStorefrontCopy";

const sections = [
  {
    title: "Information we collect",
    body: [
      "Account and contact information, including name, email address, phone number, company details, billing information, shipping addresses, and distributor application information.",
      "Order, cart, payment, shipping, warranty, return, support, and registered-item workflow information that you submit or generate while using our services.",
      "Product, vehicle, serial number, tag, RMA, image, document, invoice, CFDI, and diagnostic information when it is needed to provide service, support, warranty review, or account workflows.",
      "Device, browser, log, approximate region, language, cookie, and analytics information used to operate, secure, troubleshoot, and improve the website and native apps.",
    ],
  },
  {
    title: "How we use information",
    body: [
      "To create and manage accounts, authenticate users, process orders, manage distributor requests, support warranty/core-return workflows, and provide customer service.",
      "To calculate pricing, shipping, taxes, invoices, subscriptions, payments, credits, refunds, and account status.",
      "To protect the platform, detect misuse, maintain records, improve products and workflows, send service messages, and comply with legal or accounting obligations.",
    ],
  },
  {
    title: "How we share information",
    body: [
      "We share information with service providers that help us operate the business, including payment processors, shipping providers, email providers, analytics tools, cloud hosting, authentication providers, and app store subscription services.",
      "We may share information when required by law, to enforce our terms, to protect our business or customers, or as part of a business transfer such as a merger or asset sale.",
      "We do not sell personal information.",
    ],
  },
  {
    title: "Payments, subscriptions, and authentication",
    body: [
      "Card payments are handled by payment processors such as Stripe. We do not store full card numbers on our servers.",
      "Social sign-in may use providers such as Google, Microsoft, or Apple. Those providers process authentication information under their own privacy policies.",
      "Tech Data subscriptions and app purchases may be processed by Apple App Store or Google Play, and we receive purchase or subscription status information needed to unlock access.",
    ],
  },
  {
    title: "Cookies and analytics",
    body: [
      "We use cookies, local storage, and similar technologies to keep users signed in, remember preferences, secure the site, operate shopping cart and portal workflows, and understand site performance.",
      "Analytics tools may collect usage events, page views, device/browser details, and approximate location information. You can control cookies through your browser settings, but some features may not work without them.",
    ],
  },
  {
    title: "Security and retention",
    body: [
      "We use reasonable administrative, technical, and organizational safeguards to protect information. No internet or storage system can be guaranteed to be completely secure.",
      "We keep information for as long as needed to provide services, maintain business and accounting records, resolve disputes, support warranties and returns, comply with law, and enforce agreements.",
    ],
  },
  {
    title: "Your choices",
    body: [
      "You may request access, correction, deletion, or a copy of your personal information, subject to legal, accounting, fraud prevention, and operational record requirements.",
      "You may unsubscribe from non-transactional messages where an unsubscribe option is provided. We may still send service, security, account, order, warranty, or legal notices.",
    ],
  },
];

function PrivacySection({ title, body }) {
  return (
    <MDBox mb={3}>
      <MDTypography
        variant="h5"
        fontWeight="bold"
        mb={1.25}
        sx={{ color: "#111111" }}
      >
        {title}
      </MDTypography>
      <MDBox component="ul" sx={{ pl: 3, m: 0 }}>
        {body.map((item) => (
          <MDBox component="li" key={item} mb={1}>
            <MDTypography
              variant="body2"
              sx={{ color: "#4A4A4A", lineHeight: 1.75 }}
            >
              {item}
            </MDTypography>
          </MDBox>
        ))}
      </MDBox>
    </MDBox>
  );
}

PrivacySection.propTypes = {
  title: PropTypes.string.isRequired,
  body: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default function Privacy() {
  const { t } = useStorefrontCopy();
  return (
    <StorefrontLayout maxWidth="lg">
      <MDBox py={{ xs: 3, md: 6 }}>
        <MDBox mb={4}>
          <MDTypography
            variant="overline"
            fontWeight="bold"
            sx={{ color: "#D7261E", letterSpacing: "0.08em" }}
          >
            {t("common.brandFallback", "Storefront")}
          </MDTypography>
          <MDTypography
            variant="h2"
            fontWeight="bold"
            mt={1}
            mb={1.5}
            sx={{ color: "#111111" }}
          >
            {t("legal.privacyTitle", "Privacy Policy")}
          </MDTypography>
          <MDTypography
            variant="body1"
            sx={{ color: "#555555", maxWidth: 820, lineHeight: 1.8 }}
          >
            {t(
              "legal.privacyIntro",
              "This policy explains how this storefront collects, uses, shares, and protects customer information."
            )}
          </MDTypography>
          <MDTypography variant="body2" mt={2} sx={{ color: "#777777" }}>
            Last updated: April 30, 2026
          </MDTypography>
        </MDBox>

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: "0 14px 36px rgba(0,0,0,0.08)",
              }}
            >
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <PrivacySection
                  title="Who this policy covers"
                  body={[
                    "This policy applies to customers, distributors, staff users, vendors, website visitors, app users, and anyone who submits information through our services.",
                    "If you use a third-party service through our platform, that third party may also process information under its own privacy policy.",
                  ]}
                />
                <Divider sx={{ my: 3 }} />
                {sections.map((section, index) => (
                  <Box key={section.title}>
                    <PrivacySection title={section.title} body={section.body} />
                    {index < sections.length - 1 ? (
                      <Divider sx={{ my: 3 }} />
                    ) : null}
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              sx={{
                borderRadius: 3,
                position: { md: "sticky" },
                top: { md: 140 },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <MDTypography
                  variant="h5"
                  fontWeight="bold"
                  mb={1}
                  sx={{ color: "#111111" }}
                >
                  Contact
                </MDTypography>
                <MDTypography
                  variant="body2"
                  sx={{ color: "#555555", lineHeight: 1.7 }}
                >
                  For privacy questions or requests, contact us through your
                  account or email:
                </MDTypography>
                <MDBox mt={2}>
                  <Link
                    href="mailto:sales@vbspecialties.com"
                    underline="hover"
                    sx={{ fontWeight: 700 }}
                  >
                    sales@vbspecialties.com
                  </Link>
                </MDBox>
                <Divider sx={{ my: 3 }} />
                <MDTypography
                  variant="body2"
                  sx={{ color: "#555555", lineHeight: 1.7 }}
                >
                  Please include enough detail for us to identify your account
                  or request. We may need to verify your identity before acting
                  on privacy requests.
                </MDTypography>
                <MDBox mt={3} display="flex" flexDirection="column" gap={1}>
                  <MDButton
                    component={RouterLink}
                    to="/"
                    variant="outlined"
                    color="dark"
                    fullWidth
                  >
                    Back to home
                  </MDButton>
                  <MDButton
                    component={RouterLink}
                    to="/delete-account"
                    variant="outlined"
                    color="dark"
                    fullWidth
                  >
                    Delete account
                  </MDButton>
                  <MDButton
                    component={RouterLink}
                    to="/catalog"
                    variant="contained"
                    color="info"
                    fullWidth
                  >
                    Browse catalog
                  </MDButton>
                </MDBox>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </StorefrontLayout>
  );
}
